import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, brl, fileToBase64 } from "@/api";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Search, Printer, Copy, MessageCircle, X, Lock, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";
import ThermalReceipt from "@/components/ThermalReceipt";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { measureBlocks, planPages, sliceCanvas } from "@/utils/paginateReceipt";
import { useAuth } from "@/AuthContext";

const STATUSES = [
  "Entrada", "Orçamento", "Em diagnóstico", "Aguardando aprovação", "Em reparo",
  "Aprovado por cliente", "Aguardando peça", "Em teste",
  "Finalizado aguardando retirada", "Sem conserto", "Pronto", "Concluído / Entregue",
  "Recusado pelo cliente",
];
const STATUS_COLOR = {
  "Entrada": "bg-neutral-100 text-neutral-700",
  "Orçamento": "bg-amber-50 text-amber-700",
  "Em diagnóstico": "bg-blue-50 text-blue-700",
  "Aguardando aprovação": "bg-amber-50 text-amber-700",
  "Em reparo": "bg-purple-50 text-purple-700",
  "Aprovado por cliente": "bg-emerald-50 text-emerald-700",
  "Aguardando peça": "bg-orange-50 text-orange-700",
  "Em teste": "bg-indigo-50 text-indigo-700",
  "Finalizado aguardando retirada": "bg-teal-50 text-teal-700",
  "Sem conserto": "bg-red-50 text-red-700",
  "Pronto": "bg-green-50 text-green-700",
  "Concluído / Entregue": "bg-neutral-900 text-white",
  "Recusado pelo cliente": "bg-red-100 text-red-800 line-through",
};

const blank = {
  product_name: "", product_info: "", product_details: "", product_photo: "",
  product_code: "", customer_id: null, customer_name: "", customer_phone: "",
  customer_cpf: "", customer_address: "",
  service_value: 0, cost: 0, items: [], is_quote: false, terms: "", status: "Entrada",
  public_share: false, note: "",
};

// Soma utilitária para itens + mão-de-obra
function calcTotals(form) {
  const items = form.items || [];
  const itemsValue = items.reduce((s, i) => s + Number(i.value || 0), 0);
  const itemsCost = items.reduce((s, i) => s + Number(i.cost || 0), 0);
  const labor = Number(form.service_value || 0);
  const laborCost = Number(form.cost || 0);
  return {
    itemsValue, itemsCost, labor, laborCost,
    grandTotal: itemsValue + labor,
    grandCost: itemsCost + laborCost,
    profit: (itemsValue + labor) - (itemsCost + laborCost),
  };
}
// Para OS já salvas (recibo, listagem)
function osTotals(o) {
  const items = o.items || [];
  const itemsValue = items.reduce((s, i) => s + Number(i.value || 0), 0);
  const itemsCost = items.reduce((s, i) => s + Number(i.cost || 0), 0);
  const labor = Number(o.service_value || 0);
  const laborCost = Number(o.cost || 0);
  return {
    itemsValue, itemsCost, labor, laborCost,
    grandTotal: itemsValue + labor,
    grandCost: itemsCost + laborCost,
    profit: (itemsValue + labor) - (itemsCost + laborCost),
  };
}

function whatsappLink(phone) {
  const clean = (phone || "").replace(/\D/g, "");
  if (!clean) return null;
  return `https://wa.me/${clean.length >= 11 ? "55" + clean : clean}`;
}

function ServicePrint({ order, store, onClose }) {
  const ref = useRef(null);
  // Tamanho de fonte do bloco de Termos — vem do settings do tenant via prop "store.terms_font_size"
  const sizeMap = { xxxs: "text-[6px]", xxs: "text-[8px]", xs: "text-[9px]", sm: "text-[11px]", md: "text-[13px]", lg: "text-[15px]" };
  const termsSize = sizeMap[store?.terms_font_size || "xs"] || sizeMap.xs;
  const [busy, setBusy] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [thermalOpen, setThermalOpen] = useState(false);
  const [forceCompact, setForceCompact] = useState(false);
  const num = String(order.os_number).padStart(6, "0");

  const capture = async ({ compact = false } = {}) => {
    if (!ref.current) return null;
    try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch {}
    await new Promise(r => setTimeout(r, 150));
    const htmlEl = document.documentElement;
    const wasDark = htmlEl.classList.contains("dark");
    if (wasDark) htmlEl.classList.remove("dark");

    const clone = ref.current.cloneNode(true);
    clone.classList.add("receipt-capturing");
    if (compact) clone.classList.add("receipt-compact");
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "position:fixed;left:-99999px;top:0;background:#ffffff;z-index:-1;pointer-events:none;";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      // Mede blocos seguros antes de capturar (necessário pra paginação inteligente)
      const blocks = measureBlocks(clone);
      const canvas = await html2canvas(clone, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true,
        letterRendering: true, logging: false,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      });
      return { canvas, blocks, domHeight: clone.scrollHeight };
    } finally {
      try { document.body.removeChild(wrapper); } catch {}
      if (wasDark) htmlEl.classList.add("dark");
    }
  };

  // Compat: helpers que só precisam do canvas (imagem/share)
  const captureOnlyCanvas = async () => {
    const r = await capture({ compact: forceCompact });
    return r ? r.canvas : null;
  };

  // 3-tier inteligente igual ao Receipt.jsx
  const captureSmart = async (pagePxOf) => {
    if (forceCompact) {
      const c = await capture({ compact: true });
      if (!c) return null;
      return { ...c, mode: "compact-1p" };
    }
    const normal = await capture({ compact: false });
    if (!normal) return null;
    if (normal.canvas.height <= pagePxOf(normal)) return { ...normal, mode: "normal-1p" };
    const cmp = await capture({ compact: true });
    if (cmp && cmp.canvas.height <= pagePxOf(cmp)) return { ...cmp, mode: "compact-1p" };
    return { ...normal, mode: "normal-multi" };
  };

  // Imprime via iframe oculto com paginação inteligente em pontos seguros.
  // 3-tier: normal → compacto auto → normal + paginação segura.
  const print = async () => {
    setBusy(true);
    try {
      const A4_W_MM = 210, A4_H_MM = 297, MARGIN_MM = 6;
      const usableWidthMm = A4_W_MM - MARGIN_MM * 2;
      const usableHeightMm = A4_H_MM - MARGIN_MM * 2;
      const pagePxOf = (cap) => Math.floor((cap.canvas.width / usableWidthMm) * usableHeightMm);
      const captured = await captureSmart(pagePxOf);
      if (!captured) return;
      const { canvas, blocks, domHeight, mode } = captured;

      const pxPerMm = canvas.width / usableWidthMm;
      const pagePx = Math.floor(usableHeightMm * pxPerMm);
      const domScale = canvas.height / domHeight;
      const plan = planPages(canvas.height, pagePx, blocks, domScale);

      const pageImgs = plan.map(({ startPx, endPx }) => {
        const sub = sliceCanvas(canvas, startPx, endPx);
        const heightMm = (endPx - startPx) / pxPerMm;
        return `<div class="page"><img src="${sub.toDataURL("image/png")}" style="width:${usableWidthMm}mm;height:${heightMm}mm;"/></div>`;
      }).join("");

      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!doctype html><html><head><title>OS ${num}</title>
        <style>
          html,body{margin:0;padding:0;background:#fff;}
          @page { size: A4; margin: ${MARGIN_MM}mm; }
          .page { break-inside: avoid; page-break-inside: avoid; break-after: page; page-break-after: always; }
          .page:last-child { break-after: auto; page-break-after: auto; }
          .page img { display:block; }
          @media print { html,body{width:210mm;} .page img { width: ${usableWidthMm}mm; height:auto; } }
        </style></head>
        <body>${pageImgs}</body></html>`);
      doc.close();
      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const triggerPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {}
        setTimeout(cleanup, 1500);
      };
      const imgs = doc.querySelectorAll("img");
      let loaded = 0;
      if (imgs.length === 0) { triggerPrint(); return; }
      imgs.forEach(img => {
        if (img.complete) { loaded++; if (loaded === imgs.length) setTimeout(triggerPrint, 80); }
        else img.onload = img.onerror = () => { loaded++; if (loaded === imgs.length) setTimeout(triggerPrint, 80); };
      });
      if (mode === "compact-1p") toast.success("OS otimizada para 1 página");
      else if (mode === "normal-multi") toast.info(`OS em ${plan.length} página(s) sem cortes`);
    } finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const margin = 6;
      const pdfTmp = new jsPDF({ unit: "mm", format: "a4" });
      const maxWidth = pdfTmp.internal.pageSize.getWidth() - margin * 2;
      const maxHeight = pdfTmp.internal.pageSize.getHeight() - margin * 2;
      const pagePxOf = (cap) => Math.floor((cap.canvas.width / maxWidth) * maxHeight);
      const captured = await captureSmart(pagePxOf);
      if (!captured) return;
      const { canvas, blocks, domHeight, mode } = captured;
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pxPerMm = canvas.width / maxWidth;
      const pagePx = Math.floor(maxHeight * pxPerMm);
      const domScale = canvas.height / domHeight;

      const plan = planPages(canvas.height, pagePx, blocks, domScale);
      plan.forEach((slice, i) => {
        const sub = sliceCanvas(canvas, slice.startPx, slice.endPx);
        const sliceMm = sub.height / pxPerMm;
        if (i > 0) pdf.addPage();
        pdf.addImage(sub.toDataURL("image/png"), "PNG", margin, margin, maxWidth, sliceMm);
      });

      pdf.save(`OS-${num}.pdf`);
      if (mode === "compact-1p") toast.success("PDF otimizado em 1 página");
      else if (mode === "normal-multi") toast.info(`PDF com ${plan.length} página(s) — sem cortes`);
    } finally { setBusy(false); }
  };

  // Imagem: tenta Web Share (mobile mostra "Salvar em Fotos" + WhatsApp + e-mail). Fallback: download.
  const downloadImage = async () => {
    setBusy(true);
    try {
      const c = await captureOnlyCanvas();
      const blob = await new Promise(r => c.toBlob(b => r(b), "image/png"));
      const file = new File([blob], `OS-${num}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `OS #${num}`, text: `Recibo da OS #${num}` });
          return;
        } catch (_) { /* usuário cancelou ou navegador falhou — cai para download */ }
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `OS-${num}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Imagem baixada");
    } finally { setBusy(false); }
  };

  // WhatsApp: tenta Web Share API (escolhe direto WhatsApp). Fallback: copia imagem + abre wa.me
  const shareWhatsApp = async () => {
    setBusy(true);
    try {
      const c = await captureOnlyCanvas();
      const blob = await new Promise(r => c.toBlob(b => r(b), "image/png"));
      const file = new File([blob], `OS-${num}.png`, { type: "image/png" });
      const phone = (order.customer_phone || "").replace(/\D+/g, "");
      const waUrl = phone ? `https://wa.me/${phone.length >= 11 ? "55" + phone : phone}` : "https://wa.me/";
      const msg = `Olá ${order.customer_name || ""}! Segue o recibo da sua OS #${num}.\n\nLoja: ${store?.name || ""}`;

      // 1) Mobile: Web Share API com arquivo (anexa imagem direto)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: msg, title: `OS #${num}` });
          return;
        } catch (_) { /* segue para fallback */ }
      }

      // 2) Desktop: copia imagem pro clipboard e abre WhatsApp Web com texto explicando
      let copied = false;
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([ new ClipboardItem({ "image/png": blob }) ]);
          copied = true;
        }
      } catch (_) { /* sem permissão */ }

      const fallbackText = copied
        ? `${msg}\n\n(Imagem do recibo copiada — cole aqui com Ctrl+V / Cmd+V)`
        : msg;
      window.open(`${waUrl}?text=${encodeURIComponent(fallbackText)}`, "_blank");

      // 3) Sempre baixa a imagem como backup
      if (!copied) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `OS-${num}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Imagem baixada — anexe no WhatsApp");
      } else {
        toast.success("Imagem copiada — cole no WhatsApp com Ctrl+V");
      }
    } finally { setBusy(false); }
  };

  const t = osTotals(order);
  const items = order.items || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white max-w-md w-full max-h-[92vh] overflow-y-auto rounded-md receipt-force-light">
        <div ref={ref} className="receipt px-4 py-3" style={{ background: "#FFFFFF", color: "#0A0A0A", fontSize: "8.5px" }}>
          {/* Header — loja à esquerda, OS info à direita */}
          <div className="print-block flex items-start justify-between gap-2 border-b border-[#E5E5E5] pb-1.5 mb-1.5">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {store?.logo && <img src={store.logo} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" crossOrigin="anonymous"/>}
              <div className="min-w-0 leading-tight">
                <div className="font-display text-[11px] leading-tight">{store?.name || "Loja"}</div>
                {store?.address && <div className="text-[7px] text-neutral-500 leading-tight">{store.address}</div>}
                {store?.phone && <div className="text-[7px] text-neutral-500 leading-tight">{store.phone}</div>}
                {store?.cnpj && <div className="text-[7px] text-neutral-500 leading-tight">CNPJ: {store.cnpj}</div>}
              </div>
            </div>
            <div className="text-right flex-shrink-0 leading-tight">
              <div className="text-[6px] uppercase tracking-[0.12em] text-neutral-500">Ordem de Serviço</div>
              <div className="font-display text-[11px] leading-tight">Nº {num}</div>
              <div className="text-[7px] text-neutral-500">{new Date(order.created_at).toLocaleString('pt-BR')}</div>
              <div className="text-[7px] mt-0.5">
                <span className="text-neutral-500">Status:</span> <span className="font-medium">{order.status}</span>
              </div>
            </div>
          </div>

          {/* Cliente + Produto lado a lado */}
          <div className="print-block grid grid-cols-2 gap-1.5 mb-1.5 text-[8px] leading-tight">
            <div className="border border-[#EEE] rounded p-1 break-words">
              <div className="text-[6px] uppercase tracking-wider text-neutral-500 mb-0.5">Cliente</div>
              <div className="font-medium break-words">{order.customer_name || "—"}</div>
              {order.customer_cpf && <div className="text-neutral-700 break-words">CPF: {order.customer_cpf}</div>}
              {order.customer_address && <div className="text-neutral-700 break-words">Endereço: {order.customer_address}</div>}
              <div className="text-neutral-600 break-words">{order.customer_phone || "—"}</div>
            </div>
            <div className="border border-[#EEE] rounded p-1 min-w-0 break-words">
              <div className="text-[6px] uppercase tracking-wider text-neutral-500 mb-0.5">Produto</div>
              <div className="font-medium break-words">{order.product_name}</div>
              {order.product_code && <div className="text-neutral-600 break-words">Cód.: {order.product_code}</div>}
              {order.product_info && <div className="text-neutral-600 break-words">{order.product_info}</div>}
            </div>
          </div>

          {order.product_details && (
            <div className="print-block text-[8px] text-neutral-700 mb-1.5"><span className="font-medium">Detalhes:</span> {order.product_details}</div>
          )}

          {showPhoto && order.product_photo && (
            <img src={order.product_photo} alt="" className="print-block w-full max-h-24 object-cover rounded mb-1.5" crossOrigin="anonymous"/>
          )}

          {/* Tipo / Itens / Totais */}
          <div className="print-block border border-[#E5E5E5] rounded p-1.5 mb-1.5">
            <div className="flex justify-between text-[7px] mb-0.5">
              <span className="text-neutral-500">Tipo</span>
              <span className="font-medium">{order.is_quote ? "Orçamento (sem valor fechado)" : "Serviço"}</span>
            </div>
            {!order.is_quote && (
              <>
                {items.length > 0 && (
                  <div className="border-t border-dashed border-[#E5E5E5] pt-0.5 mt-0.5">
                    <div className="text-[6px] uppercase tracking-wider text-neutral-500 mb-0.5">Peças e serviços</div>
                    {items.map((it, i) => (
                      <div key={i} className="flex justify-between gap-2 text-[8px] py-0.5">
                        <span className="break-words flex-1 min-w-0">{it.description || `Item ${i+1}`}</span>
                        <span className="font-display whitespace-nowrap">{brl(Number(it.value || 0))}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-[7px] text-neutral-500 mt-0.5 pt-0.5 border-t border-dashed border-[#E5E5E5]">
                      <span>Subtotal peças</span>
                      <span className="font-display">{brl(t.itemsValue)}</span>
                    </div>
                  </div>
                )}
                {t.labor > 0 && (
                  <div className="flex justify-between text-[8px] mt-0.5">
                    <span className="text-neutral-500">Mão de obra</span>
                    <span className="font-display">{brl(t.labor)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[10px] mt-1 pt-1 border-t border-[#E5E5E5]">
                  <span className="font-medium">Total</span>
                  <span className="font-display font-medium">{brl(t.grandTotal)}</span>
                </div>
              </>
            )}
          </div>

          {order.terms && (
            <div className={`print-block ${termsSize} leading-[1.4] text-neutral-700 border border-[#E5E5E5] rounded p-2 mb-2 whitespace-pre-line`}>
              <div className="font-medium mb-1 text-[10px] uppercase tracking-wider">Termos e Condições</div>
              {order.terms}
            </div>
          )}

          <div className="print-block grid grid-cols-2 gap-3 mt-3">
            <div className="text-center">
              <div className="border-t border-neutral-700 mt-4 pt-0.5 text-[7px] break-words">{order.customer_name || "Cliente"}</div>
              <div className="text-[6px] text-neutral-500">Assinatura do cliente</div>
            </div>
            <div className="text-center">
              <div className="border-t border-neutral-700 mt-4 pt-0.5 text-[7px] break-words">{store?.name || "Loja"}</div>
              <div className="text-[6px] text-neutral-500">Assinatura da loja</div>
            </div>
          </div>
        </div>

        {/* Toggles + ações */}
        {order.product_photo && (
          <div className="px-4 py-2 border-t border-[#E5E5E5] flex items-center justify-between">
            <Label htmlFor="show-photo-toggle" className="text-xs flex items-center gap-2 cursor-pointer">
              <Checkbox id="show-photo-toggle" checked={showPhoto} onCheckedChange={(v)=>setShowPhoto(!!v)} data-testid="receipt-show-photo"/>
              Incluir foto do produto no recibo
            </Label>
          </div>
        )}

        <div className="px-3 pt-2 pb-1">
          <label className="flex items-center justify-between gap-2 text-[11px] cursor-pointer select-none rounded-md border border-dashed border-[#E5E5E5] hover:bg-neutral-50 px-2.5 py-1.5" data-testid="os-compact-mode-toggle">
            <span className="flex items-center gap-2">
              <Printer size={12} className={forceCompact ? "text-emerald-600" : "text-neutral-500"}/>
              <span className="font-medium">Modo compacto</span>
            </span>
            <input
              type="checkbox"
              checked={forceCompact}
              onChange={(e)=>setForceCompact(e.target.checked)}
              className="h-3.5 w-3.5 accent-emerald-600 cursor-pointer"
              data-testid="os-compact-mode-checkbox"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2 px-3 pt-3 pb-1 border-t border-[#E5E5E5]">
          <Button variant="outline" onClick={print} disabled={busy} size="sm" data-testid="receipt-print"><Printer size={14} className="mr-1"/> Imprimir</Button>
          <Button onClick={downloadPDF} disabled={busy} size="sm" className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="receipt-pdf">PDF</Button>
        </div>
        <div className="px-3 pt-1">
          <Button variant="outline" onClick={()=>setThermalOpen(true)} size="sm" className="w-full border-dashed" data-testid="receipt-print-thermal"><Printer size={14} className="mr-1"/> Imprimir Cupom (térmica)</Button>
        </div>
        <div className="grid grid-cols-3 gap-2 px-3 pt-1 pb-3">
          <Button variant="outline" onClick={downloadImage} disabled={busy} size="sm" data-testid="receipt-image">
            <ImageIcon size={14} className="mr-1"/> Imagem
          </Button>
          <Button onClick={shareWhatsApp} disabled={busy} size="sm" className="bg-green-500 hover:bg-green-600 text-white" data-testid="receipt-whatsapp">
            <MessageCircle size={14} className="mr-1"/> WhatsApp
          </Button>
          <Button variant="outline" onClick={onClose} size="sm"><X size={14} className="mr-1"/> Fechar</Button>
        </div>
      </div>
      {thermalOpen && (
        <ThermalReceipt data={order} type="service" store={store} onClose={()=>setThermalOpen(false)}/>
      )}
    </div>
  );
}

export default function Assistencia() {
  const { canSeeFinance } = useAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState("");
  const [termsFontSize, setTermsFontSize] = useState("xs"); // xs | sm | md | lg
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [store, setStore] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);

  // Customer creation inline
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", parental_phone: "", address: "" });

  const load = async () => {
    const [o, c, s, st] = await Promise.all([
      api.get("/service/orders"),
      api.get("/customers"),
      api.get("/service/settings"),
      api.get("/store"),
    ]);
    setList(o.data); setCustomers(c.data);
    setDefaultTerms(s.data.default_terms || "");
    setTermsFontSize(s.data.terms_font_size || "xs");
    setStore(st.data || {});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let l = list;
    if (filter !== "all") l = l.filter(o => o.status === filter);
    if (search.trim()) {
      const t = search.trim().toLowerCase();
      l = l.filter(o =>
        (o.product_name || "").toLowerCase().includes(t) ||
        (o.customer_name || "").toLowerCase().includes(t) ||
        String(o.os_number).padStart(6, "0").includes(t)
      );
    }
    return l;
  }, [list, filter, search]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => {
    setForm({ ...blank, terms: defaultTerms });
    setEditingId(null); setOpen(true);
  };
  const openEdit = (o) => {
    setForm({ ...blank, ...o });
    setEditingId(o.id); setOpen(true);
  };

  const onPickCustomer = (cid) => {
    if (cid === "new") { setCreatingCustomer(true); return; }
    if (cid === "none") {
      update("customer_id", null);
      update("customer_name", "");
      update("customer_phone", "");
      update("customer_cpf", "");
      update("customer_address", "");
      return;
    }
    const c = customers.find(x => x.id === cid);
    if (c) {
      update("customer_id", c.id);
      update("customer_name", c.name);
      update("customer_phone", c.phone);
      update("customer_cpf", c.cpf || "");
      update("customer_address", c.address || "");
    }
  };

  const submitNewCustomer = async () => {
    if (!newCust.name || !newCust.phone) return toast.error("Nome e telefone obrigatórios");
    const r = await api.post("/customers", newCust);
    setCustomers(prev => [...prev, r.data]);
    update("customer_id", r.data.id);
    update("customer_name", r.data.name);
    update("customer_phone", r.data.phone);
    setCreatingCustomer(false);
    setNewCust({ name: "", phone: "", parental_phone: "", address: "" });
    toast.success("Cliente criado");
  };

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    update("product_photo", await fileToBase64(f));
  };

  const submit = async () => {
    if (!form.product_name) return toast.error("Nome do produto obrigatório");
    if (!form.customer_name || !form.customer_phone) return toast.error("Cliente obrigatório (com telefone)");
    try {
      const cleanItems = (form.items || [])
        .filter(it => (it.description || "").trim() || Number(it.value || 0) > 0 || Number(it.cost || 0) > 0)
        .map(it => ({
          description: (it.description || "").trim() || "Item",
          value: Number(it.value || 0),
          cost: Number(it.cost || 0),
        }));
      const payload = {
        ...form,
        service_value: Number(form.service_value || 0),
        cost: Number(form.cost || 0),
        items: cleanItems,
      };
      if (editingId) await api.put(`/service/orders/${editingId}`, payload);
      else await api.post("/service/orders", payload);
      setOpen(false);
      toast.success("OS salva");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir OS?")) return;
    await api.delete(`/service/orders/${id}`);
    load();
  };

  const updatePayment = async (o, payment_method) => {
    try {
      const r = await api.put(`/service/orders/${o.id}`, { payment_method });
      const updated = r.data || {};
      // Atualiza estado local sem recarregar tudo
      setList(prev => prev.map(x => x.id === o.id ? { ...x, ...updated, payment_method } : x));
      const chargeable = ["Finalizado aguardando retirada", "Pronto", "Concluído / Entregue"].includes(updated.status || o.status);
      if (payment_method === "Pendente" && o.customer_id && chargeable) {
        toast.success("Pagamento atualizado — saldo devedor lançado no cliente");
      } else if (payment_method === "Pendente" && o.customer_id) {
        toast.success("Pagamento atualizado — saldo será lançado quando OS for finalizada");
      } else {
        toast.success("Pagamento atualizado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar pagamento");
    }
  };

  const sendPixWhats = async (o) => {
    const phone = (o.customer_phone || "").replace(/\D+/g, "");
    if (!phone) return toast.error("Cliente sem telefone cadastrado");
    let token = o.public_token;
    if (!token) {
      try {
        const r = await api.put(`/service/orders/${o.id}`, { public_share: true });
        token = r.data?.public_token;
        setList(prev => prev.map(x => x.id === o.id ? { ...x, ...(r.data || {}) } : x));
      } catch {
        toast.error("Não foi possível gerar o link público");
        return;
      }
    }
    const wa = phone.length >= 11 ? `55${phone}` : phone;
    const num = String(o.os_number).padStart(6, "0");
    const link = `${window.location.origin}/servico/${token}`;
    const msg = `Olá, segue o link de pagamento do seu conserto OS: #${num}\n${link}`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const updateStatus = async (o, status) => {
    try {
      const r = await api.put(`/service/orders/${o.id}`, { status });
      setList(prev => prev.map(x => x.id === o.id ? { ...x, ...(r.data || {}), status } : x));
      const chargeable = ["Finalizado aguardando retirada", "Pronto", "Concluído / Entregue"].includes(status);
      if (chargeable && o.payment_method === "Pendente" && o.customer_id) {
        toast.success("Status atualizado — saldo devedor lançado no cliente");
      } else {
        toast.success("Status atualizado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const togglePublic = async (o) => {
    const r = await api.put(`/service/orders/${o.id}`, { public_share: !o.public_share });
    toast.success(r.data.public_share ? "Link público gerado" : "Link público desativado");
    load();
  };

  const copyLink = async (o) => {
    const url = `${window.location.origin}/servico/${o.public_token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const sendWhats = (o) => {
    const wa = whatsappLink(o.customer_phone);
    if (!wa) return toast.error("Cliente sem telefone");
    const url = o.public_token
      ? `${window.location.origin}/servico/${o.public_token}`
      : null;
    const text = url
      ? `Olá ${o.customer_name}! Acompanhe sua OS #${String(o.os_number).padStart(6,'0')}: ${url}`
      : `Olá ${o.customer_name}! Sua OS #${String(o.os_number).padStart(6,'0')} — Status: ${o.status}`;
    window.open(`${wa}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      await api.put("/service/settings", { default_terms: defaultTerms, terms_font_size: termsFontSize });
      toast.success("Termos salvos");
      setShowSettings(false);
    } finally { setSettingsBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="assistencia-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-tiny mb-2">Operação</p>
          <h1 className="font-display text-4xl tracking-tight">Assistência</h1>
          <p className="text-sm text-neutral-500 mt-1">Ordens de serviço, status e termos.</p>
        </div>
        <div className="flex gap-2">
          <HideMoneyButton testId="assistencia-hide-money"/>
          <Button variant="outline" onClick={()=>setShowSettings(true)} data-testid="open-settings-button">Termos padrão</Button>
          <Button onClick={openNew} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-os-button"><Plus size={16} className="mr-1"/> Nova OS</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por OS, cliente ou produto…" className="pl-9" data-testid="os-search-input"/>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="os-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {filtered.map(o => (
          <Card key={o.id} className="p-4 border-[#E5E5E5] shadow-none" data-testid={`os-${o.id}`}>
            <div className="flex flex-wrap gap-3 justify-between items-start">
              <div className="flex gap-3 min-w-0 flex-1">
                {o.product_photo && <img src={o.product_photo} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0"/>}
                <div className="min-w-0">
                  <div className="text-xs font-mono text-neutral-500">OS #{String(o.os_number).padStart(6,'0')}</div>
                  <div className="font-medium mt-0.5 truncate">{o.product_name}</div>
                  <div className="text-xs text-neutral-500 truncate">{o.customer_name} {o.customer_phone && `· ${o.customer_phone}`}</div>
                  <div className="text-[11px] text-neutral-400">{new Date(o.created_at).toLocaleString('pt-BR')}</div>
                  {(o.created_by || o.last_actor) && (
                    <div className="text-[9px] text-neutral-300 italic">
                      {o.created_by && `criado: ${o.created_by}`}
                      {o.created_by && o.last_actor && o.last_actor !== o.created_by && " · "}
                      {o.last_actor && o.last_actor !== o.created_by && `alterado: ${o.last_actor}`}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || "bg-neutral-100 text-neutral-700"}`}>{o.status}</span>
                    {o.is_quote
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Orçamento</span>
                      : <span className="text-xs text-neutral-700 font-display"><Money value={osTotals(o).grandTotal}/></span>}
                    {(o.items?.length > 0) && !o.is_quote && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{o.items.length} {o.items.length === 1 ? "item" : "itens"}</span>
                    )}
                    {canSeeFinance && !o.is_quote && (osTotals(o).grandCost > 0 || osTotals(o).grandTotal > 0) && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        Lucro: <Money value={osTotals(o).profit}/>
                      </span>
                    )}
                    {o.public_token && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Link ativo</span>}
                    {o.payment_method && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700">
                        {o.payment_method === "Pendente programado" ? "Crediário" : o.payment_method}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 flex-wrap">
                <Select value={o.status} onValueChange={(v)=>updateStatus(o, v)}>
                  <SelectTrigger className="h-8 text-xs w-44" data-testid={`os-status-${o.id}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={()=>setPrinting(o)} title="Imprimir / PDF"><Printer size={12}/></Button>
                <Button size="sm" variant="outline" onClick={()=>togglePublic(o)} title={o.public_share ? "Desativar link público" : "Ativar link público"}>{o.public_share ? "🔗" : "🔒"}</Button>
                {o.public_token && <Button size="sm" variant="outline" onClick={()=>copyLink(o)} title="Copiar link"><Copy size={12}/></Button>}
                <Button size="sm" variant="outline" onClick={()=>sendWhats(o)} title="Enviar no WhatsApp"><MessageCircle size={12}/></Button>
                <Button size="sm" variant="ghost" onClick={()=>openEdit(o)}><Edit size={12}/></Button>
                <Button size="sm" variant="ghost" onClick={()=>remove(o.id)}><Trash2 size={12}/></Button>
              </div>
            </div>

            {/* Forma de pagamento + botão WhatsApp para PIX (canto inferior direito) */}
            <div className="mt-3 pt-3 border-t border-[#F0F0F0] flex flex-wrap items-center justify-end gap-2">
              <Label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0">
                Forma de pagamento
              </Label>
              <Select
                value={o.payment_method || "none"}
                onValueChange={(v)=>updatePayment(o, v === "none" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-xs w-40" data-testid={`os-payment-${o.id}`}>
                  <SelectValue placeholder="—"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Não definido —</SelectItem>
                  <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="Cartão de débito">Cartão de débito</SelectItem>
                  <SelectItem value="Cartão de crédito">Cartão de crédito</SelectItem>
                  <SelectItem value="Boleto">Boleto</SelectItem>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                </SelectContent>
              </Select>
              {o.payment_method === "PIX" && (
                <Button
                  size="sm"
                  className="h-8 bg-green-500 hover:bg-green-600 text-white"
                  onClick={()=>sendPixWhats(o)}
                  data-testid={`os-pix-whatsapp-${o.id}`}
                >
                  <MessageCircle size={12} className="mr-1"/> Enviar PIX no WhatsApp
                </Button>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center py-12 text-sm text-neutral-500">Nenhuma OS encontrada.</div>}
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? `Editar OS #${String(form.os_number || "").padStart(6,'0')}` : "Nova ordem de serviço"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="produto">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="produto">Produto</TabsTrigger>
              <TabsTrigger value="cliente">Cliente</TabsTrigger>
              <TabsTrigger value="termos">Valor & Termos</TabsTrigger>
            </TabsList>
            <TabsContent value="produto" className="mt-4 space-y-3">
              <div><Label>Produto</Label><Input value={form.product_name} onChange={e=>update("product_name", e.target.value)} placeholder="Ex: Carretilha Shimano Curado 200K" data-testid="os-product-name"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Código do produto</Label><Input value={form.product_code} onChange={e=>update("product_code", e.target.value)}/></div>
                <div>
                  <Label>OS Nº</Label>
                  <Input value={editingId ? String(form.os_number || "").padStart(6,'0') : "(automático)"} disabled/>
                </div>
              </div>
              <div><Label>Estado físico do produto</Label><Input value={form.product_info} onChange={e=>update("product_info", e.target.value)} placeholder="Ex.: marcas de uso, arranhões, riscos, dentes"/></div>
              <div><Label>Defeito / Solução</Label><Textarea value={form.product_details} onChange={e=>update("product_details", e.target.value)} rows={3} placeholder="Diagnóstico, observações, peças, etc."/></div>
              <div>
                <Label>Foto do produto (opcional)</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.product_photo
                    ? <img src={form.product_photo} alt="" className="w-16 h-16 rounded object-cover border border-[#E5E5E5]"/>
                    : <div className="w-16 h-16 rounded border border-dashed border-[#E5E5E5] flex items-center justify-center text-[10px] text-neutral-400">sem foto</div>}
                  <Input type="file" accept="image/*" onChange={handlePhoto} data-testid="os-photo-input"/>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="cliente" className="mt-4 space-y-3">
              <div>
                <Label>Selecionar cliente</Label>
                <Select value={form.customer_id || "none"} onValueChange={onPickCustomer}>
                  <SelectTrigger data-testid="os-customer-select"><SelectValue placeholder="Selecione"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    <SelectItem value="new">+ Cadastrar novo cliente</SelectItem>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.phone}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.customer_name && (
                <Card className="p-3 border-[#E5E5E5] shadow-none bg-neutral-50">
                  <div className="text-sm font-medium">{form.customer_name}</div>
                  <div className="text-xs text-neutral-500">{form.customer_phone}</div>
                  {form.customer_phone && (
                    <a href={whatsappLink(form.customer_phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-green-700 underline mt-1">
                      <MessageCircle size={12}/> Abrir WhatsApp
                    </a>
                  )}
                </Card>
              )}
            </TabsContent>
            <TabsContent value="termos" className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="is_quote" checked={form.is_quote} onCheckedChange={(v)=>update("is_quote", !!v)} data-testid="os-quote-checkbox"/>
                <Label htmlFor="is_quote" className="cursor-pointer">É um Orçamento (sem valor fechado)</Label>
              </div>

              {!form.is_quote && (
                <>
                  {/* Peças e serviços */}
                  <div className="border border-[#E5E5E5] rounded-md p-3 space-y-2" data-testid="os-items-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider text-neutral-700">Peças e serviços</Label>
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => update("items", [...(form.items || []), { description: "", value: 0, cost: 0 }])}
                        data-testid="add-item-button">
                        <Plus size={12} className="mr-1"/> Adicionar item
                      </Button>
                    </div>
                    <p className="text-[11px] text-neutral-500">Ex.: parachoque, pintura lateral, troca de óleo. Cliente vê descrição + valor; custo é interno.</p>

                    {(form.items || []).length === 0 && (
                      <div className="text-xs text-neutral-400 italic py-3 text-center border border-dashed border-[#E5E5E5] rounded">
                        Sem peças. Clique em "Adicionar item" para incluir.
                      </div>
                    )}

                    {(form.items || []).map((it, idx) => (
                      <div key={idx} className="border border-[#E5E5E5] rounded-md p-2.5 bg-neutral-50/40 space-y-2" data-testid={`os-item-${idx}`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <Label className="text-[10px] text-neutral-500">Descrição</Label>
                            <Input
                              value={it.description || ""}
                              onChange={(e) => {
                                const items = [...form.items];
                                items[idx] = { ...items[idx], description: e.target.value };
                                update("items", items);
                              }}
                              placeholder="Ex.: Parachoque dianteiro"
                              data-testid={`item-desc-${idx}`}
                            />
                          </div>
                          <Button
                            type="button" size="icon" variant="ghost"
                            className="text-red-600 mt-5"
                            onClick={() => update("items", form.items.filter((_, i) => i !== idx))}
                            data-testid={`remove-item-${idx}`}>
                            <Trash2 size={14}/>
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-neutral-500">Valor (cliente vê)</Label>
                            <CurrencyInput
                              value={it.value || 0}
                              onChange={(v) => {
                                const items = [...form.items];
                                items[idx] = { ...items[idx], value: v };
                                update("items", items);
                              }}
                              data-testid={`item-value-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-amber-800 flex items-center gap-1"><Lock size={9}/> Custo (interno)</Label>
                            <CurrencyInput
                              value={it.cost || 0}
                              onChange={(v) => {
                                const items = [...form.items];
                                items[idx] = { ...items[idx], cost: v };
                                update("items", items);
                              }}
                              data-testid={`item-cost-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label>Mão de obra (extra, opcional)</Label>
                    <CurrencyInput value={form.service_value} onChange={(v)=>update("service_value", v)} data-testid="os-value-input"/>
                    <p className="text-[11px] text-neutral-500 mt-1">Soma com os itens para formar o total cobrado.</p>
                  </div>
                </>
              )}

              <div className="border border-dashed border-amber-300 bg-amber-50/40 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Lock size={12} className="text-amber-700"/>
                  <Label className="text-amber-900 text-xs uppercase tracking-wider">Custo extra do reparo (interno)</Label>
                </div>
                <CurrencyInput value={form.cost || 0} onChange={(v)=>update("cost", v)} data-testid="os-cost-input"/>
                <p className="text-[11px] text-amber-800/80 mt-1">Visível apenas para a equipe. Custos dos itens já entram no total separadamente.</p>
              </div>

              {!form.is_quote && (() => {
                const t = calcTotals(form);
                return (
                  <div className="border border-[#0A0A0A] bg-neutral-50 rounded-md p-3 space-y-1 text-sm" data-testid="os-totals-summary">
                    <div className="flex justify-between"><span className="text-neutral-500">Subtotal peças</span><span className="font-display">{brl(t.itemsValue)}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-500">Mão de obra</span><span className="font-display">{brl(t.labor)}</span></div>
                    <div className="flex justify-between border-t border-[#E5E5E5] pt-1 mt-1"><span className="font-medium">Total faturado</span><span className="font-display text-base">{brl(t.grandTotal)}</span></div>
                    {canSeeFinance && (
                      <>
                        <div className="flex justify-between text-amber-800"><span>Custo total (interno)</span><span className="font-display">{brl(t.grandCost)}</span></div>
                        <div className="flex justify-between text-emerald-700 font-medium"><span>Lucro estimado</span><span className="font-display">{brl(t.profit)}</span></div>
                      </>
                    )}
                  </div>
                );
              })()}

              <div>
                <Label>Termos de garantia / condições</Label>
                <Textarea value={form.terms} onChange={e=>update("terms", e.target.value)} rows={6} placeholder="Cole aqui os termos. Se quiser definir um padrão, use o botão 'Termos padrão' na tela principal." data-testid="os-terms-input"/>
              </div>
              <div className="flex items-center gap-2 border-t border-[#E5E5E5] pt-3">
                <Checkbox id="public" checked={form.public_share} onCheckedChange={(v)=>update("public_share", !!v)} data-testid="os-public-checkbox"/>
                <Label htmlFor="public" className="cursor-pointer text-sm">Gerar link público para o cliente acompanhar status</Label>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v)=>update("status", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-os-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New customer dialog */}
      <Dialog open={creatingCustomer} onOpenChange={setCreatingCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Novo cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={newCust.name} onChange={e=>setNewCust({...newCust, name: e.target.value})}/></div>
            <div><Label>Telefone *</Label><Input value={newCust.phone} onChange={e=>setNewCust({...newCust, phone: e.target.value})} placeholder="(00) 00000-0000"/></div>
            <div><Label>Telefone parental (opcional)</Label><Input value={newCust.parental_phone} onChange={e=>setNewCust({...newCust, parental_phone: e.target.value})} placeholder="Pai/responsável"/></div>
            <div><Label>Endereço</Label><Input value={newCust.address} onChange={e=>setNewCust({...newCust, address: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreatingCustomer(false)}>Cancelar</Button>
            <Button onClick={submitNewCustomer} className="bg-[#0A0A0A] hover:bg-black text-white">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Default terms settings */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Termos de garantia padrão</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Estes termos aparecem automaticamente como padrão ao criar uma nova OS. Você pode editar em cada OS individualmente.</p>
          <Textarea value={defaultTerms} onChange={e=>setDefaultTerms(e.target.value)} rows={10} data-testid="default-terms-input"/>
          <div className="mt-3">
            <p className="text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">Tamanho da fonte no recibo</p>
            <div className="flex gap-1.5 flex-wrap">
              {[{k:"xxxs",l:"Ultra pequeno"},{k:"xxs",l:"Bem pequeno"},{k:"xs",l:"Pequena"},{k:"sm",l:"Média"},{k:"md",l:"Grande"},{k:"lg",l:"Muito grande"}].map(o => (
                <button
                  key={o.k}
                  type="button"
                  onClick={()=>setTermsFontSize(o.k)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition ${termsFontSize === o.k ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"}`}
                  data-testid={`os-font-${o.k}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className={`mt-2 p-2 border border-dashed border-[#E5E5E5] rounded text-neutral-700 ${({xxxs:'text-[6px]',xxs:'text-[8px]',xs:'text-[9px]',sm:'text-[11px]',md:'text-[13px]',lg:'text-[15px]'})[termsFontSize]} whitespace-pre-line max-h-24 overflow-y-auto`}>
              <div className="font-medium mb-1 text-[10px] uppercase tracking-wider">Termos e Condições</div>
              {defaultTerms || "(visualização do tamanho — escreva o termo acima)"}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowSettings(false)}>Cancelar</Button>
            <Button onClick={saveSettings} disabled={settingsBusy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-terms-button">{settingsBusy ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {printing && <ServicePrint
        order={printing}
        store={{ ...store, terms_font_size: termsFontSize }}
        onClose={()=>setPrinting(null)}
      />}
    </div>
  );
}
